import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, Loader2, RefreshCw } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('sage_token')}` });

const fmtSleep = (m) => (m ? `${Math.floor(m / 60)}h ${m % 60}m` : null);

const Metric = ({ label, value, testId }) => (
  <div className="p-3 bg-black/20 rounded-sm" data-testid={testId}>
    <p className="text-lg font-light text-white">{value ?? '—'}</p>
    <p className="text-[11px] text-zinc-500">{label}</p>
  </div>
);

const OuraMetrics = ({ s }) => (
  <div className="grid grid-cols-2 gap-2 mb-3">
    <Metric label={`Sleep${fmtSleep(s.total_sleep_minutes) ? ` · ${fmtSleep(s.total_sleep_minutes)}` : ''}`} value={s.sleep_score} testId="oura-sleep" />
    <Metric label="Readiness" value={s.readiness_score} testId="oura-readiness" />
    <Metric label="HRV (ms)" value={s.average_hrv} testId="oura-hrv" />
    <Metric label="Resting HR (bpm)" value={s.resting_heart_rate} testId="oura-rhr" />
    <Metric label="Steps" value={s.steps != null ? s.steps.toLocaleString() : null} testId="oura-steps" />
    <Metric label="Stress · Resilience" value={[s.stress_summary, s.resilience_level].filter(Boolean).join(' · ') || null} testId="oura-stress" />
  </div>
);

export const OuraCard = () => {
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadToday = async (refresh = false) => {
    const { data } = await axios.get(`${API}/oura/today${refresh ? '?refresh=true' : ''}`, { headers: authHeaders() });
    setSnapshot(data.snapshot);
  };

  useEffect(() => {
    axios.get(`${API}/oura/status`, { headers: authHeaders() })
      .then(({ data }) => {
        setStatus(data);
        if (data.connected) loadToday().catch(() => {});
      })
      .catch(() => setStatus({ eligible: false }));
  }, []);

  if (!status?.eligible) return null;

  const connect = async () => {
    setBusy(true);
    try {
      const redirect = `${window.location.origin}/dashboard`;
      const { data } = await axios.get(`${API}/oura/connect`, { params: { redirect_uri: redirect }, headers: authHeaders() });
      window.location.assign(data.authorize_url);
    } catch {
      toast.error('Could not start the Oura connection.');
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try { await loadToday(true); } catch { toast.error('Could not refresh Oura data.'); }
    setBusy(false);
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/oura/disconnect`, { headers: authHeaders() });
      setStatus({ ...status, connected: false });
      setSnapshot(null);
      toast.success('Oura disconnected and its data deleted.');
    } catch {
      toast.error('Could not disconnect Oura.');
    }
    setBusy(false);
  };

  return (
    <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6" data-testid="oura-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Body Signals
        </h3>
        {status.connected ? (
          <button type="button" onClick={refresh} disabled={busy} aria-label="Refresh Oura data" data-testid="oura-refresh-button" className="text-zinc-400 hover:text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
        ) : <Activity className="w-5 h-5 text-amber-300" />}
      </div>

      {!status.connected || status.needs_reconnect ? (
        <>
          <p className="text-sm text-zinc-400 mb-4">
            {status.needs_reconnect
              ? 'Your Oura link expired. Reconnect so SAGE can see how rested you are.'
              : 'Connect your Oura ring so SAGE can gently factor in your unique daily biometrics.'}
          </p>
          <button type="button" onClick={connect} disabled={busy} data-testid="oura-connect-button" className="frost-button w-full rounded-sm py-2 text-sm">
            {busy ? 'Opening Oura…' : status.needs_reconnect ? 'Reconnect Oura' : 'Connect Oura'}
          </button>
        </>
      ) : (
        <>
          {snapshot ? <OuraMetrics s={snapshot} /> : <p className="text-sm text-zinc-500 mb-3" data-testid="oura-empty">No recent data from your ring yet.</p>}
          {snapshot?.day && <p className="text-[11px] text-zinc-500 mb-3" data-testid="oura-day">From {snapshot.day}</p>}
          <button type="button" onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="oura-disconnect-button" className="text-xs text-zinc-500 hover:text-zinc-300 underline">
            Disconnect &amp; delete Oura data
          </button>
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="oura-disconnect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Oura?</AlertDialogTitle>
            <AlertDialogDescription>
              SAGE will stop reading your ring and delete all Oura data it stored, including the notes in past session summaries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="oura-disconnect-cancel">Keep connected</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect} data-testid="oura-disconnect-confirm">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OuraCard;
